import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, Plus, Ban } from 'lucide-react'
import {
  couponRedemptions, createCoupon, createDiscount, deactivateCoupon,
  listCoupons, listDiscounts, majorToMinor, revokeDiscount,
  type Coupon, type Discount,
} from '@/api/billingAdmin'
import { AdminModal, Field, InfoBox, Td, Th, chipStyle, errText, tableStyle } from '../adminUi'

const SCOPES = [
  { key: 'plans', label: 'Plan purchases' },
  { key: 'packs', label: 'Credit packs' },
  { key: 'runtime', label: 'Runtime user seats' },
]

export default function CouponsDiscountsPanel() {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [discounts, setDiscounts] = useState<Discount[]>([])
  const [loading, setLoading] = useState(true)
  const [creatingCoupon, setCreatingCoupon] = useState(false)
  const [creatingDiscount, setCreatingDiscount] = useState(false)
  const [viewing, setViewing] = useState<Coupon | null>(null)

  const load = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([listCoupons(), listDiscounts()])
      setCoupons(c.coupons)
      setDiscounts(d.discounts)
    } catch (e) {
      toast.error(errText(e, 'Could not load coupons and discounts'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <InfoBox>
        A <strong>discount</strong> is granted to one user and applies
        automatically at checkout. A <strong>coupon</strong> is a code anyone can
        redeem. Both are applied before tax, discount first and then the coupon on
        the already-discounted amount.
      </InfoBox>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Coupons</h3>
          <button className="btn btn-primary" onClick={() => setCreatingCoupon(true)}>
            <Plus size={14} /> New coupon
          </button>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th>Code</Th><Th>Value</Th><Th>Applies to</Th><Th>Valid until</Th>
              <Th right>Used</Th><Th right>Limit</Th><Th right>Active</Th><Th />
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id}>
                <Td><span style={{ ...chipStyle, fontFamily: 'monospace' }}>{c.code}</span></Td>
                <Td>
                  {c.percent_off ? `${c.percent_off}% off`
                    : c.fixed_off_minor ? `${c.fixed_off_currency || ''} ${(c.fixed_off_minor / 100).toFixed(2)} off`
                    : c.fixed_off_credits ? `${c.fixed_off_credits} credits off` : '—'}
                </Td>
                <Td>{c.scope?.length ? c.scope.join(', ') : 'everything'}</Td>
                <Td>{c.valid_until ? new Date(c.valid_until).toLocaleDateString() : '—'}</Td>
                <Td right>
                  <button className="btn btn-secondary" style={{ padding: '0.1rem 0.4rem' }}
                          onClick={() => setViewing(c)}>
                    {c.redemptions_count}
                  </button>
                </Td>
                <Td right>{c.max_redemptions ?? '∞'}</Td>
                <Td right>{c.is_active ? 'Yes' : 'No'}</Td>
                <Td right>
                  {c.is_active && (
                    <button className="btn btn-secondary" style={{ padding: '0.15rem 0.4rem' }}
                            title="Deactivate — coupons are never deleted, so past orders stay auditable"
                            onClick={async () => {
                              try { await deactivateCoupon(c.id); await load() }
                              catch (e) { toast.error(errText(e, 'Could not deactivate')) }
                            }}>
                      <Ban size={12} />
                    </button>
                  )}
                </Td>
              </tr>
            ))}
            {!coupons.length && (
              <tr><Td style={{ color: 'var(--color-text-secondary)' }}>No coupons yet.</Td></tr>
            )}
          </tbody>
        </table>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.375rem' }}>
          A coupon's terms are fixed once created — only deactivation is possible,
          so a code someone already redeemed can never change meaning after the fact.
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Per-user discounts</h3>
          <button className="btn btn-primary" onClick={() => setCreatingDiscount(true)}>
            <Plus size={14} /> Grant discount
          </button>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th>User</Th><Th>Value</Th><Th>Applies to</Th><Th>Until</Th>
              <Th right>Used</Th><Th>Note</Th><Th right>Active</Th><Th />
            </tr>
          </thead>
          <tbody>
            {discounts.map((d) => (
              <tr key={d.id}>
                <Td>{d.all_users ? <strong>All users</strong> : (d.email || d.user_id)}</Td>
                <Td>
                  {d.percent_off ? `${d.percent_off}%`
                    : d.fixed_off_minor ? (d.fixed_off_minor / 100).toFixed(2)
                    : d.fixed_off_credits ? `${d.fixed_off_credits} credits` : '—'}
                </Td>
                <Td>
                  {d.scope?.length ? d.scope.join(', ') : 'everything'}
                  {d.applies_period ? ` · ${d.applies_period}` : ''}
                </Td>
                <Td>{d.valid_until ? new Date(d.valid_until).toLocaleDateString() : '—'}</Td>
                <Td right>{d.uses_count}{d.max_uses ? ` / ${d.max_uses}` : ''}</Td>
                <Td>{d.note || '—'}</Td>
                <Td right>{d.is_active ? 'Yes' : 'No'}</Td>
                <Td right>
                  {d.is_active && (
                    <button className="btn btn-secondary" style={{ padding: '0.15rem 0.4rem' }}
                            onClick={async () => {
                              try { setDiscounts((await revokeDiscount(d.id)).discounts) }
                              catch (e) { toast.error(errText(e, 'Could not revoke')) }
                            }}>
                      <Ban size={12} />
                    </button>
                  )}
                </Td>
              </tr>
            ))}
            {!discounts.length && (
              <tr><Td style={{ color: 'var(--color-text-secondary)' }}>No discounts granted.</Td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creatingCoupon && (
        <CouponForm onClose={() => setCreatingCoupon(false)}
                    onSaved={async () => { setCreatingCoupon(false); await load() }} />
      )}
      {creatingDiscount && (
        <DiscountForm onClose={() => setCreatingDiscount(false)}
                      onSaved={async () => { setCreatingDiscount(false); await load() }} />
      )}
      {viewing && <RedemptionsModal coupon={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}

function ScopePicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
      {SCOPES.map((s) => (
        <label key={s.key} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={value.includes(s.key)}
            onChange={() => onChange(
              value.includes(s.key) ? value.filter((v) => v !== s.key) : [...value, s.key],
            )}
          />
          {s.label}
        </label>
      ))}
    </div>
  )
}

function CouponForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [code, setCode] = useState('')
  const [kind, setKind] = useState<'percent' | 'fixed'>('percent')
  const [value, setValue] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [scope, setScope] = useState<string[]>([])
  const [validUntil, setValidUntil] = useState('')
  const [maxRedemptions, setMaxRedemptions] = useState('')
  const [perUser, setPerUser] = useState('1')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!code.trim() || !value) { toast.error('A code and a value are required'); return }
    setSaving(true)
    try {
      await createCoupon({
        code: code.trim().toUpperCase(),
        percent_off: kind === 'percent' ? parseFloat(value) : undefined,
        fixed_off_minor: kind === 'fixed' ? majorToMinor(value) : undefined,
        fixed_off_currency: kind === 'fixed' ? currency : undefined,
        scope: scope.length ? scope : undefined,
        valid_until: validUntil || undefined,
        max_redemptions: maxRedemptions ? parseInt(maxRedemptions, 10) : undefined,
        per_user_limit: parseInt(perUser, 10) || 1,
      })
      toast.success('Coupon created')
      await onSaved()
    } catch (e) {
      toast.error(errText(e, 'Could not create the coupon'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminModal title="New coupon" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        <Field label="Code" hint="Upper-cased automatically. Cannot be changed later.">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                 placeholder="WELCOME20" style={{ width: '100%', fontFamily: 'monospace' }} />
        </Field>

        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr 1fr' }}>
          <Field label="Type">
            <select value={kind} onChange={(e) => setKind(e.target.value as 'percent' | 'fixed')} style={{ width: '100%' }}>
              <option value="percent">Percent off</option>
              <option value="fixed">Fixed amount off</option>
            </select>
          </Field>
          <Field label={kind === 'percent' ? 'Percent' : 'Amount'}>
            <input type="number" min={0} step="0.01" value={value}
                   onChange={(e) => setValue(e.target.value)} style={{ width: '100%' }} />
          </Field>
          {kind === 'fixed' && (
            <Field label="Currency" hint="A fixed amount only applies in its own currency.">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ width: '100%' }}>
                {['USD', 'INR', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}
        </div>

        <div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.375rem' }}>
            Applies to (none ticked = everything)
          </div>
          <ScopePicker value={scope} onChange={setScope} />
        </div>

        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr 1fr' }}>
          <Field label="Valid until"><input type="date" value={validUntil}
                 onChange={(e) => setValidUntil(e.target.value)} style={{ width: '100%' }} /></Field>
          <Field label="Total redemptions" hint="Blank = unlimited.">
            <input type="number" min={1} value={maxRedemptions}
                   onChange={(e) => setMaxRedemptions(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Per user">
            <input type="number" min={1} value={perUser}
                   onChange={(e) => setPerUser(e.target.value)} style={{ width: '100%' }} />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : 'Create coupon'}
          </button>
        </div>
      </div>
    </AdminModal>
  )
}

function DiscountForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [allUsers, setAllUsers] = useState(false)
  const [email, setEmail] = useState('')
  const [value, setValue] = useState('')
  const [scope, setScope] = useState<string[]>([])
  const [appliesPeriod, setAppliesPeriod] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if ((!allUsers && !email.trim()) || !value) {
      toast.error('Pick a user (or All users) and enter a value')
      return
    }
    setSaving(true)
    try {
      await createDiscount({
        email: allUsers ? undefined : email.trim(),
        all_users: allUsers,
        percent_off: parseFloat(value),
        scope: scope.length ? scope : undefined,
        applies_period: appliesPeriod || undefined,
        valid_from: validFrom || undefined,
        valid_until: validUntil || undefined,
        note: note.trim() || undefined,
      })
      toast.success('Discount granted')
      await onSaved()
    } catch (e) {
      toast.error(errText(e, 'Could not grant the discount'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminModal title="Grant a discount" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.875rem' }}>
          <input type="checkbox" checked={allUsers}
                 onChange={(e) => setAllUsers(e.target.checked)} />
          All users — a platform-wide promotion (e.g. a yearly-subscription
          discount for everyone)
        </label>
        {!allUsers && (
          <Field label="User email" hint="Applied automatically at that user's checkout.">
            <input value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="user@example.com" style={{ width: '100%' }} />
          </Field>
        )}
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
          <Field label="Percent off">
            <input type="number" min={0} max={100} step="0.01" value={value}
                   onChange={(e) => setValue(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Billing period" hint="Restrict to monthly or yearly plan purchases.">
            <select value={appliesPeriod} onChange={(e) => setAppliesPeriod(e.target.value)}
                    style={{ width: '100%' }}>
              <option value="">Any</option>
              <option value="monthly">Monthly only</option>
              <option value="yearly">Yearly only</option>
            </select>
          </Field>
        </div>
        <div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.375rem' }}>
            Applies to (none ticked = everything)
          </div>
          <ScopePicker value={scope} onChange={setScope} />
        </div>
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr 2fr' }}>
          <Field label="Valid from" hint="Blank = immediately.">
            <input type="date" value={validFrom}
                   onChange={(e) => setValidFrom(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Valid until" hint="Blank = no end date.">
            <input type="date" value={validUntil}
                   onChange={(e) => setValidUntil(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Note" hint="Shown in this table so a later admin knows why.">
            <input value={note} onChange={(e) => setNote(e.target.value)} style={{ width: '100%' }} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : 'Grant discount'}
          </button>
        </div>
      </div>
    </AdminModal>
  )
}

function RedemptionsModal({ coupon, onClose }: { coupon: Coupon; onClose: () => void }) {
  const [rows, setRows] = useState<Array<{ user_id: string; email: string | null; redeemed_at: string | null }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    couponRedemptions(coupon.id)
      .then((d) => setRows(d.redemptions))
      .catch(() => toast.error('Could not load redemptions'))
      .finally(() => setLoading(false))
  }, [coupon.id])

  return (
    <AdminModal title={`${coupon.code} redemptions`} onClose={onClose}>
      {loading ? <Loader2 className="spin" /> : !rows.length ? (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem' }}>
          Not redeemed yet.
        </p>
      ) : (
        <table style={tableStyle}>
          <thead><tr><Th>User</Th><Th>Redeemed</Th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <Td>{r.email || r.user_id}</Td>
                <Td>{r.redeemed_at ? new Date(r.redeemed_at).toLocaleString() : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </AdminModal>
  )
}
